import React, { FC, useCallback, useMemo, useState } from 'react';
import { Skeleton, Table } from 'src/common';
import { ColumnProps, TablePaginationConfig } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import {
  isAdminCompaniesSortField,
  AdminCompaniesSortVars,
  AdminCompaniesSortField
} from 'src/models/admin/Companies/types';
import styles from '../UserList/index.module.scss';
import dayjs from 'src/helpers/dayjs';
import TableFilter from './TableFilter';
import { SorterResult, SortOrder, FilterValue } from 'antd/lib/table/interface';
import { GetComponentProps } from 'rc-table/lib/interface';
import { useFetchMore } from 'src/common/infinityScroll';
import { LoadMoreButton } from 'src/shared';
import LinkToCompanyInfo from '../LinkToCompanyInfo';
import { PricePlanSelect } from '../RoleSelect/PricePlanSelect';
import { FeatureRoleSelect } from '../RoleSelect/FeatureRoleSelect';
import { ICompany } from 'src/models/company/types';
import { useAdminCompanies, useAdminCompaniesVars } from 'src/models/admin/Companies/hooks';
import { AdminCompany } from 'src/models/admin/CompanyUsers';
import { AdminCompaniesProvider } from 'src/models/admin/Companies/providers';

// FIXME: @cheeck if it's email?? prob ID?
interface AdminCompaniesColumnProps {
  id: string;
}
export const CompaniesList: FC = () => (
  <AdminCompaniesProvider>
    <CompaniesListContent />
  </AdminCompaniesProvider>
);
export const CompaniesListContent: FC = () => {
  const { t } = useTranslation();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [{ args: searchArgs }, sortCompanies] = useAdminCompaniesVars();
  const sorting = searchArgs.sortBy;
  const onChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<AdminCompaniesColumnProps> | SorterResult<AdminCompaniesColumnProps>[]
    ): void => {
      const prepareSortData = (sorter: SorterResult<AdminCompaniesColumnProps>): AdminCompaniesSortVars | undefined => {
        const { order } = sorter;
        const apiOrder = order === 'ascend' ? 'ASC' : 'DESC';
        const column = sorter.column;

        return column?.key && isAdminCompaniesSortField(column.key) && order
          ? { column: column.key, sort: apiOrder }
          : undefined;
      };

      if (Array.isArray(sorter)) {
        console.error('sorter is array. We do not support this');
      } else {
        const sortData = prepareSortData(sorter);
        sortCompanies({ sortBy: sortData });
      }
    },
    [sortCompanies]
  );

  const { loading, data: companiesList, fetchMore, fetchingMore } = useAdminCompanies();

  const columns: ColumnProps<AdminCompany>[] = useMemo(
    () => [
      {
        title: t('Company.company'),
        dataIndex: 'companyName',
        className: styles.regularCol,
        fixed: 'left',
        sorter: true,
        key: 'companyName',
        sortOrder: mapSortingToSortOrder('companyName', sorting),
        render: companyName => {
          return <>{companyName || '—'}</>;
        }
      },
      {
        title: t('Company.orgNumber'),
        dataIndex: 'orgNumber',
        className: styles.regularCol,
        sorter: true,
        key: 'orgNumber',
        sortOrder: mapSortingToSortOrder('orgNumber', sorting),
        render: orgNumber => {
          return <>{orgNumber || '—'}</>;
        }
      },
      {
        title: t('AdminPage.companyId'),
        dataIndex: 'id',
        className: styles.wideCol,
        sorter: true,
        key: 'id',
        sortOrder: mapSortingToSortOrder('id', sorting),
        render: companyId => {
          return <>{companyId ? <LinkToCompanyInfo companyId={companyId} /> : '—'}</>;
        }
      },
      {
        title: t('AdminPage.pricePlan'),
        dataIndex: 'pricePlan',
        className: styles.wideCol,
        sorter: true,
        key: 'pricePlan',
        sortOrder: mapSortingToSortOrder('pricePlan', sorting),
        render: (pricePlan, record) => {
          return <PricePlanSelect companyId={record.id} pricePlan={pricePlan} />;
        }
      },
      {
        title: t('AdminPage.featureRole'),
        dataIndex: 'featureRoles',
        className: styles.wideCol,
        sorter: true,
        key: 'featureRoles',
        sortOrder: mapSortingToSortOrder('featureRoles', sorting),
        render: (featureRoles, record) => {
          return <FeatureRoleSelect companyId={record.id} featureRoles={featureRoles} />;
        }
      },
      {
        title: t('AdminPage.mainAccount'),
        dataIndex: 'mainUser',
        className: styles.wideCol,
        key: 'mainUser',
        render: mainUser => {
          return <>{mainUser || '—'}</>;
        }
      },
      {
        title: t('Common.createdOn'),
        dataIndex: 'createdOn',
        className: styles.colDate,
        sorter: true,
        key: 'createdOn',
        sortOrder: mapSortingToSortOrder('createdOn', sorting)
      },
      {
        title: '',
        dataIndex: 'id',
        fixed: 'right',
        className: styles.colAction,
        render: companyId => {
          return <LinkToCompanyInfo companyId={companyId} mode={'button'} />;
        }
      }
    ],
    [t, sorting]
  );

  const companies = useMemo(() => companiesList?.companies || [], [companiesList?.companies]);

  const data = useMemo(() => {
    return companies.map(apiCompanies => {
      return {
        id: apiCompanies.id,
        companyName: apiCompanies.companyName,
        createdOn: dayjs(apiCompanies.createdOn).format('YYYY-MM-DD'),
        mainUser: apiCompanies.createdBy?.email || null,
        orgNumber: apiCompanies.orgNumber,
        pricePlan: apiCompanies.pricePlan,
        featureRoles: apiCompanies.featureRoles
      };
    });
  }, [companies]);

  const total = companiesList?.count || 0;
  const hasMore = data.length < total;

  const loadMore = useCallback(
    async (variables: { offset: number }) => {
      return fetchMore({
        variables
      });
    },
    [fetchMore]
  );

  const onLoadMore = useFetchMore({ offset: companiesList?.companies.length || 0 }, loadMore);

  function getRowId(id: string): string {
    return `companyRow-${id}`;
  }

  const onRow: GetComponentProps<ICompany> = useCallback(record => {
    return {
      id: getRowId(record.id)
    };
  }, []);

  return (
    <div ref={setContainer} className={styles.container}>
      <div className={styles.overviewCard}>
        <TableFilter />
        {loading ? (
          <Skeleton loading active />
        ) : (
          <Table
            rowKey={'id'}
            columns={columns}
            dataSource={data}
            scroll={{ x: 1500 }}
            onRow={onRow}
            className={styles.table}
            rowClassName={styles.row}
            onChange={onChange}
            footer={() => (
              <LoadMoreButton
                onLoadMore={onLoadMore}
                isLoading={fetchingMore}
                hasMore={hasMore}
                scrollContainer={container}
              />
            )}
          />
        )}
      </div>
    </div>
  );
};

function mapSortingToSortOrder(column: AdminCompaniesSortField, sorting?: AdminCompaniesSortVars): SortOrder {
  if (!sorting) {
    return null;
  }
  if (sorting.column === column) {
    switch (sorting.sort) {
      case 'ASC': {
        return 'ascend';
      }
      case 'DESC': {
        return 'descend';
      }
    }
  }
  return null;
}

export default CompaniesList;
